/**
 * Handlers de Erros para o Frontend
 * Captura erros do Vue, HTTP, JavaScript e Promises
 */

import type { App } from 'vue';
import { discordService } from './service';
import type { DiscordError } from './types';

let vueHandlerInitialized = false;
let javascriptHandlerInitialized = false;
let promiseHandlerInitialized = false;

function shouldIgnoreError(message?: string) {
  if (!message) return false;

  const normalizedMessage = message.toLowerCase();

  return normalizedMessage.includes('resizeobserver loop completed with undelivered notifications');
}

/**
 * Configurar captura de erros do Vue
 */
export function setupVueErrorHandler(app: App) {
  if (vueHandlerInitialized) {
    return;
  }

  vueHandlerInitialized = true;

  app.config.errorHandler = (err, instance, info) => {
    console.error('Vue Error:', err);

    const discordError: DiscordError = {
      type: 'vue',
      error: err,
      context: {
        componentName: instance?.$options.name || instance?.$options.__name || 'Unknown Component',
        lifecycle: info,
        route: window.location.pathname,
        url_page: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date(),
      },
      severity: 'high',
    };

    // Enviar para Discord
    discordService.notifyError(discordError);
  };

  console.log('✅ Vue error handler configurado');
}

/**
 * Configurar captura de erros JavaScript não tratados
 */
export function setupJavaScriptErrorHandler() {
  if (javascriptHandlerInitialized) {
    return;
  }

  javascriptHandlerInitialized = true;

  window.addEventListener('error', (event) => {
    const errorMessage = event.error?.message || event.message;
    if (shouldIgnoreError(errorMessage)) {
      return;
    }

    console.error('JavaScript Error:', errorMessage || event.error || event);

    const discordError: DiscordError = {
      type: 'javascript',
      error: event.error || new Error(event.message),
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        url_page: window.location.href,
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        timestamp: new Date(),
      },
      severity: 'medium',
    };

    discordService.notifyError(discordError);
  });

  console.log('✅ JavaScript error handler configurado');
}

/**
 * Configurar captura de Promises rejeitadas não tratadas
 */
export function setupPromiseErrorHandler() {
  if (promiseHandlerInitialized) {
    return;
  }

  promiseHandlerInitialized = true;

  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled Promise Rejection:', event.reason);

    const discordError: DiscordError = {
      type: 'promise',
      error: event.reason,
      context: {
        url_page: window.location.href,
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        timestamp: new Date(),
      },
      severity: 'medium',
    };

    discordService.notifyError(discordError);
  });

  console.log('✅ Promise error handler configurado');
}

/**
 * Configurar todas as capturas de erro
 */
export function setupDiscordErrorHandlers(app: App) {
  setupVueErrorHandler(app);
  setupJavaScriptErrorHandler();
  setupPromiseErrorHandler();
  
  console.log('🎉 Sistema de notificações Discord configurado!');
}

